<template>
  <div class="question-group-container">
    <!-- Header with Progress -->
    <div class="progress-header">
      <div class="container">
        <div class="header-content">
          <div class="progress-info">
            <span class="progress-label">Total de puntos: </span>
            <span
              class="progress-label"
              :class="['total-points', totalPointsClass]"
            >
              {{ totalPoints }} / 100
            </span>
          </div>
          <b-progress
            :value="progressPercentage"
            :type="progressType"
            size="is-small"
          />
          <p class="remaining-text">
            {{ remainingPointsText }}
          </p>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="container main-content">
      <!-- Title Section -->
      <div class="section-header">
        <h1
          class="title is-spaced is-size-3-desktop is-size-4-mobile has-text-weight-bold mb-2"
        >
          {{
            questions[0].id.includes("2") ? "Cultura deseada" : "Cultura Actual"
          }}
        </h1>
        <p class="subtitle is-size-5-desktop is-size-6-mobile">
          {{ group.text || "-" }}
        </p>
      </div>

      <!-- Questions List -->
      <div class="questions-list">
        <div
          v-for="(question, index) in questions"
          :key="question.id"
          class="question-card"
        >
          <label class="question-label">
            <strong>{{ indexQuestion[index] }}</strong
            >. {{ question.text }}
          </label>
          <div class="question-controls">
            <b-field class="is-fullwidth">
              <b-numberinput
                :value="points[groupNumber][question.id]"
                step="5"
                min="0"
                max="100"
                expanded
                @input="(value) => handlePointChange(question.id, value)"
              >
              </b-numberinput>
            </b-field>
            <span class="points-display"> {{ points[question.id] }} pts </span>
          </div>
        </div>
      </div>

      <!-- Submit Button -->
      <div class="section-buttons mt-4">
        <b-button
          type="is-primary"
          size="is-medium"
          expanded
          v-if="groupNumber > 0"
          :disabled="isLoading"
          @click="handleBack"
        >
          Volver
        </b-button>
        <b-button
          type="is-primary"
          size="is-medium"
          expanded
          :disabled="!isValid || isLoading"
          :loading="isLoading"
          @click="handleSubmit"
        >
          {{ isLoading ? "Guardando..." : "Siguiente" }}
        </b-button>
      </div>
    </div>

    <!-- Sticky Footer with Progress -->
    <div class="progress-footer">
      <div class="container">
        <div class="header-content">
          <div class="progress-info">
            <span class="progress-label"
              >Página {{ groupNumber + 1 }} de {{ group.sizeGroups }}</span
            >
            <span class="progress-label">
              Progreso General: {{ generalProgress }}%
            </span>
          </div>
          <b-progress
            :value="generalProgress"
            size="is-small"
            type="is-primary"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "QuestionGroup",
  props: {
    survey: {
      type: Object,
      required: true,
    },
    group: {
      type: Object,
      required: true,
    },
    groupNumber: {
      type: Number,
      required: true,
    },
    questions: {
      type: Array,
      required: true,
    },
  },
  data() {
    let points = {};

    if (this.survey.responses) {
      points = this.survey.responses;
    }

    if (!points[this.groupNumber]) {
      const init = this.questions.reduce((acc, q) => {
        acc[q.id] = 0;
        return acc;
      }, {});
      points[this.groupNumber] = init;
    }

    return {
      indexQuestion: ["A", "B", "C", "D"],
      points,
      currentPoints: points[this.groupNumber],
      isLoading: false,
    };
  },
  computed: {
    totalPoints() {
      return Object.values(this.currentPoints).reduce((a, b) => a + b, 0);
    },
    remainingPoints() {
      return 100 - this.totalPoints;
    },
    isValid() {
      return this.totalPoints === 100;
    },
    progressPercentage() {
      return Math.min(100, this.totalPoints);
    },
    generalProgress() {
      const value = (
        (this.groupNumber / this.group.sizeGroups || 0) * 100
      ).toFixed(0);
      return parseInt(value);
    },
    summaryClass() {
      if (this.totalPoints === 100) return "is-success";
      if (this.totalPoints > 100) return "is-danger";
      return "";
    },
    totalPointsClass() {
      if (this.totalPoints === 100) return "has-text-success";
      if (this.totalPoints > 100) return "has-text-danger";
      return "";
    },
    progressType() {
      if (this.totalPoints === 100) return "is-success";
      if (this.totalPoints > 100) return "is-danger";
      return "is-primary";
    },
    remainingPointsText() {
      if (this.remainingPoints > 0) {
        return `Te quedan ${this.remainingPoints} puntos por distribuir`;
      }

      if (this.remainingPoints === 0) {
        return `Todos los puntos asignados correctamente`;
      }

      return `Has excedido por ${Math.abs(this.remainingPoints)} puntos`;
    },
  },
  methods: {
    handlePointChange(questionId, value) {
      const numValue = parseInt(value) || 0;
      const clampedValue = Math.max(0, Math.min(100, numValue));
      this.$set(this.currentPoints, questionId, clampedValue);
      this.$set(this.points[this.groupNumber], questionId, clampedValue);
    },
    handleSubmit() {
      if (!this.isValid) return;

      this.isLoading = true;
      this.$emit("complete", {
        type: "group",
        groupNumber: this.groupNumber,
        answers: this.points,
      });
      this.isLoading = false;
    },
    handleBack() {
      this.$emit("back", {
        type: "group",
        groupNumber: this.groupNumber,
      });
    },
  },
  watch: {
    groupNumber(newVal) {
      if (!this.points[newVal]) {
        const initPoints = this.questions.reduce((acc, q) => {
          acc[q.id] = 0;
          return acc;
        }, {});
        this.$set(this.points, newVal, initPoints);
      }

      this.currentPoints = this.points[newVal] || {};
    },
  },
};
</script>

<style scoped>
.question-group-container {
  min-height: 100vh;
  background-color: #f5f5f5;
}

/* Progress Header */
.progress-header {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: white;
  border-bottom: 1px solid #dbdbdb;
  padding: 1rem 0;
}

.header-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.progress-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #7a7a7a;
}

/* Main Content */
.main-content {
  max-width: 1200px;
  padding: 1.5rem 1rem;
}

/* Progress Footer */
.progress-footer {
  /* position: sticky;
  bottom: 0;
  z-index: 10; */
  background-color: white;
  border-bottom: 1px solid #dbdbdb;
  padding: 1rem 0;
}

.total-points {
  font-weight: bold;
}

.total-points.has-text-success {
  color: #48c78e;
}

.total-points.has-text-danger {
  color: #f14668;
}

.remaining-text {
  font-size: 0.875rem;
  color: #7a7a7a;
  margin-top: -1rem;
  margin-bottom: 0;
}

/* Section Header */
.section-header {
  margin-bottom: 1.5rem;
}

/* Questions List */
.questions-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.question-card {
  background-color: white;
  border: 1px solid #dbdbdb;
  border-radius: 6px;
  padding: 1.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.3s ease;
}

.question-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.question-label {
  display: block;
  font-size: 1.125rem;
  font-weight: 500;
  margin-bottom: 1rem;
  color: #363636;
}

.question-controls {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
}

.points-display {
  min-width: 70px;
  font-size: 1rem;
  font-weight: 500;
  color: #7a7a7a;
}

.section-buttons {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
}

/* Responsive */
@media screen and (max-width: 768px) {
  .main-content {
    padding: 1rem;
  }

  .question-card {
    padding: 1rem;
  }

  .question-label {
    font-size: 1rem;
  }

  .question-controls {
    flex-wrap: wrap;
  }
}

.is-fullwidth {
  width: 100%;
}
</style>



// WEBPACK FOOTER //
// src/components/cultura/sections/survey/QuestionGroup.vue